import { useEffect, useState } from 'react'
import Title from '../../common/Title'
import { useIntl } from 'react-intl'
import Card from '../../common/Card'
import { useMutation } from '@tanstack/react-query'
import { importFileOperations } from '../../../api/operation'
import { useGetProgramFilter } from '../../../hooks/useGetProgramFilter'
import useIsProcessingImport from '../../../hooks/useIsProcessingImport/useIsProcessingImport'
import { useAppSelector } from '../../../store/hooks'
import Alert from 'ui/Alert'
import { Select } from 'ui/fields'
import FileUploadButton from '../../common/FileUploadButton'
import { createDateFromISO } from 'utils/dates'
import { getAlertType } from '../../../utils/partnerUtils'
import useFileUpload from '../../../hooks/useFIleUpload/useFileUpload'
import { toast } from 'react-toastify'

function ImportOperations() {
   const [error, setError] = useState(null)
   const { data: programs, isLoading } = useGetProgramFilter()
   const { data: dataImported, isDone, reset } = useIsProcessingImport({ type: 'OPERATIONS' })
   const { locale } = useAppSelector((state) => state.base)
   const intl = useIntl()
   const { mutate: submitForm } = useMutation({
      mutationFn: importFileOperations,
   })
   const onSucces = () => {
      toast.info(intl.formatMessage({ id: 'operations.import.success' }))
   }
   const onError = (e: any) => {
      toast.error(intl.formatMessage({ id: 'operations.import.error' }))
   }
   const { showStatusBanner, programId, setProgramId, file, setFile, handleFileChange } = useFileUpload(
      submitForm,
      0,
      onSucces,
      onError,
   )
   useEffect(() => {
      setError(null)
      setFile(null)
   }, [programId, setFile])

   const isLoadingImport =
      dataImported?.result?.status === 'PENDING' || showStatusBanner || dataImported?.result?.status === 'IN_PROGRESS'
   return (
      <>
         <Title
            className="flex justify-between gap-x-2  items-center "
            value={intl.formatMessage({ id: 'operations.import' })}
         ></Title>
         <div className="flex flex-col gap-y-4 mx-4">
            <Card className="p-5">
             {dataImported?.result && !isDone && (
                  <Alert className="m-5" type={getAlertType(dataImported.result.status)}>
                     <Alert.Body className="font-semibold flex items-center gap-3">
                        <svg
                           className="animate-spin h-5 w-5 text-blue-500 shrink-0"
                           xmlns="http://www.w3.org/2000/svg"
                           fill="none"
                           viewBox="0 0 24 24"
                        >
                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <div>
                           <div>
                              {dataImported.result.status === 'IN_PROGRESS'
                                 ? 'Procesando importación...'
                                 : 'Importación en cola, espere...'}
                           </div>
                           <div className="text-sm font-normal text-gray-500 mt-1">
                              Este proceso puede tardar unos momentos. No cierre esta ventana.
                           </div>
                        </div>
                     </Alert.Body>
                  </Alert>
               )}

               {dataImported?.result && isDone && (
                  <Alert className="m-5" type={getAlertType(dataImported.result.status)}>
                     <Alert.Body className="font-semibold">
                        <div>{dataImported.message}</div>
                        <div>
                           {intl.formatMessage(
                              { id: 'partner.createAt' },
                              {
                                 date: createDateFromISO(dataImported.result.createdAt)
                                    .setLocale(locale)
                                    .toFormat("cccc d 'de' LLLL 'del' yyyy"),
                              },
                           )}
                        </div>
                        <button
                           onClick={reset}
                           className="mt-3 text-sm underline font-normal text-blue-600 hover:text-blue-800"
                        >
                           Realizar otra importación
                        </button>
                     </Alert.Body>
                  </Alert>
               )}

               <Select
                  label={intl.formatMessage({ id: 'program.planName' })}
                  value={programId}
                  onChange={setProgramId}
                  showErrorText={true}
                  className="mb-4"
                  disabled={isLoadingImport || isLoading}
               >
                  <Select.Option value="">--</Select.Option>
                  {programs?.items.map((program) => (
                     <Select.Option key={program.id} value={program.id}>
                        {program.name}
                     </Select.Option>
                  ))}
               </Select>
               {!!programId && (
                  <FileUploadButton
                     handleFileChange={handleFileChange}
                     loading={isLoadingImport}
                     file={file}
                     setFile={setFile}
                     error={error}
                     setError={setError}
                     helpText={intl.formatMessage({ id: 'operations.importHelpText' })}
                  />
               )}
            </Card>
         </div>
      </>
   )
}

export default ImportOperations
